import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, Calendar, Clock, Video, BookOpen, FileText, Award, Target, Users, ChevronRight } from 'lucide-react';

const LandingPage = () => {
  const [selectedPlan, setSelectedPlan] = useState('both');

  useEffect(() => {
    // Set page title
    document.title = 'Dès le Début - A1 & A2 Live French Coaching | Shikshann';
  }, []);

  const handleEnrollClick = (plan) => {
    setSelectedPlan(plan);
    // In a real implementation, this would navigate to enrollment
    console.log(`Enrolling in plan: ${plan}`);
  };

  const handleClarityCall = () => {
    // In a real implementation, this would open a booking calendar
    console.log('Booking clarity call');
  };

  return (
    <div className="landing-page">
      <Helmet>
        <title>Dès le Début - A1 & A2 Live French Coaching | Shikshann</title>
        <meta name="description" content="Live instructor-led French coaching program for A1 & A2 levels. Get DELF/TEF/TCF exam-ready with 200 hours of structured learning. Perfect for Canada PR, France jobs, and study abroad." />
        <link rel="canonical" href="https://learn.shikshann.com/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Dès le Début - A1 & A2 Live French Coaching | Shikshann" />
        <meta property="og:description" content="Live instructor-led French coaching program for A1 & A2 levels. Get DELF/TEF/TCF exam-ready with 200 hours of structured learning." />
        <meta property="og:url" content="https://learn.shikshann.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://learn.shikshann.com/og-image.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dès le Début - A1 & A2 Live French Coaching | Shikshann" />
        <meta name="twitter:description" content="Live instructor-led French coaching program for A1 & A2 levels. Get DELF/TEF/TCF exam-ready with 200 hours of structured learning." />
        <meta name="twitter:image" content="https://learn.shikshann.com/og-image.jpg" />
      </Helmet>

      {/* Header */}
      <header className="site-header">
        <div className="container-custom">
          <div className="header-content">
            <div className="logo-section">
              <img 
                src="https://lh3.googleusercontent.com/gps-cs-s/AHRPTWk6vfS4AJafHrNA61n8_hYjb9pXyLv-V-wDbQRzl5jbkgre_949q_KMY16v3DLxyUsdIFeMRNO-QRs8UtGUS4FZT49-OcMKyHDjlcXEe6XHadzWQ1HzXuCJRW9GVc3BHrqTnM1YeHAuddzY=s800"
                alt="Shikshann Logo"
                className="logo-image-header"
              />
            </div>
            <nav className="nav-menu">
              <a href="#program" className="nav-link">Program Details</a>
              <a href="#pricing" className="nav-link">Pricing</a>
              <a href="#coach" className="nav-link">About Coach</a>
              <a href="#faq" className="nav-link">FAQ</a>
            </nav>
            <a href="#pricing">
              <Button className="cta-button">
                Reserve Your Spot
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main>
      <section className="hero-section">
        <div className="container-custom">
          <div className="hero-content">
            <article className="hero-text">
              <Badge className="hero-badge">Live French Coaching • A1 & A2</Badge>
              <h1 className="hero-headline">
                <span className="headline-line">Dreaming of a Job in France?</span><br />
                <span className="headline-line">A Life in Canada?</span>
              </h1>
              <p className="hero-subheadline">
                Either way, French comes first.<br />Your journey starts here.
              </p>
              <div className="hero-cta-group">
                <a href="#pricing">
                  <Button size="lg" className="cta-primary">
                    Reserve Your Spot
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
                <a href="https://shikshann.co/book-a-call" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="cta-secondary">
                    Book a Free Clarity Call
                  </Button>
                </a>
              </div>
              <div className="social-proof">
                <div className="proof-item">
                  <Users className="proof-icon" />
                  <span>750+ Students Taught</span>
                </div>
                <div className="proof-item">
                  <Award className="proof-icon" />
                  <span>14 Years Experience</span>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Program Details Section */}
      <section id="program" className="program-section">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Complete A1 & A2 French Program</h2>
            <p className="section-subtitle">
              200 hours of live, structured coaching to take you from complete beginner to DELF/TEF/TCF exam-ready
            </p>
          </div>

          <div className="program-grid">
            <Card className="program-card level-card">
              <CardHeader>
                <Badge className="level-badge a1-badge">A1 Level</Badge>
                <CardTitle className="card-title">Complete Beginner</CardTitle>
                <CardDescription className="card-description">100 hours • Sept 21 - Dec 19, 2026</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="feature-list">
                  <li><CheckCircle2 className="check-icon" />Build foundation from zero</li>
                  <li><CheckCircle2 className="check-icon" />Master basic conversations</li>
                  <li><CheckCircle2 className="check-icon" />CEFR A1 aligned curriculum</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="program-card level-card">
              <CardHeader>
                <Badge className="level-badge a2-badge">A2 Level</Badge>
                <CardTitle className="card-title">Elementary</CardTitle>
                <CardDescription className="card-description">100 hours • Dec 21, 2026 - Mar 20, 2027</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="feature-list">
                  <li><CheckCircle2 className="check-icon" />Expand vocabulary & grammar</li>
                  <li><CheckCircle2 className="check-icon" />Handle everyday situations</li>
                  <li><CheckCircle2 className="check-icon" />CEFR A2 aligned curriculum</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="program-card schedule-card">
              <CardHeader>
                <CardTitle className="card-title">Schedule & Format</CardTitle>
              </CardHeader>
              <CardContent className="schedule-content">
                <div className="schedule-item">
                  <Calendar className="schedule-icon" />
                  <div>
                    <p className="schedule-label">Class Days</p>
                    <p className="schedule-value">Mon, Tue, Wed, Fri</p>
                  </div>
                </div>
                <div className="schedule-item">
                  <Clock className="schedule-icon" />
                  <div>
                    <p className="schedule-label">Time</p>
                    <p className="schedule-value">7:00 - 9:00 PM IST</p>
                  </div>
                </div>
                <div className="schedule-item">
                  <Video className="schedule-icon" />
                  <div>
                    <p className="schedule-label">Format</p>
                    <p className="schedule-value">100% Live on Zoom</p>
                  </div>
                </div>
                <div className="schedule-item">
                  <Target className="schedule-icon" />
                  <div>
                    <p className="schedule-label">Outcome</p>
                    <p className="schedule-value">DELF/TEF/TCF Ready</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="cta-section-inline">
            <a href="#pricing">
              <Button size="lg" className="cta-primary">
                Reserve Your Spot
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="included-section">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Everything You Need to Succeed</h2>
            <p className="section-subtitle">Complete learning ecosystem included with your enrollment</p>
          </div>

          <div className="included-grid">
            <Card className="included-card">
              <CardContent className="included-content">
                <div className="included-icon-wrapper">
                  <Video className="included-icon" />
                </div>
                <h3 className="included-title">Session Recordings</h3>
                <p className="included-description">
                  Never miss a class. Access all recordings for review and revision anytime.
                </p>
              </CardContent>
            </Card>

            <Card className="included-card">
              <CardContent className="included-content">
                <div className="included-icon-wrapper">
                  <BookOpen className="included-icon" />
                </div>
                <h3 className="included-title">LMS Access</h3>
                <p className="included-description">
                  Track your progress, access materials, and stay organized in one place.
                </p>
              </CardContent>
            </Card>

            <Card className="included-card">
              <CardContent className="included-content">
                <div className="included-icon-wrapper">
                  <FileText className="included-icon" />
                </div>
                <h3 className="included-title">Complete Learning Kit</h3>
                <p className="included-description">
                  Textbook, workbook, handouts, worksheets & online practice tools.
                </p>
              </CardContent>
            </Card>

            <Card className="included-card">
              <CardContent className="included-content">
                <div className="included-icon-wrapper">
                  <Award className="included-icon" />
                </div>
                <h3 className="included-title">Exam Alignment</h3>
                <p className="included-description">
                  DELF/TEF/TCF exam patterns built into the curriculum from day one.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="cta-section-inline">
            <a href="#pricing">
              <Button size="lg" className="cta-primary">
                Reserve Your Spot
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Investment in Your Future</h2>
            <p className="section-subtitle">Choose the path that fits your timeline and goals</p>
          </div>

          <div className="pricing-grid">
            <Card className="pricing-card">
              <CardHeader>
                <CardTitle className="pricing-title">A1 & A2 Separately</CardTitle>
                <CardDescription className="pricing-description">Individual Level Purchase</CardDescription>
                <div className="pricing-amount">
                  <span className="currency">₹</span>
                  <span className="amount">26,000</span>
                  <span className="each-label"> each</span>
                </div>
                <p className="pricing-period">100 hours per level</p>
              </CardHeader>
              <CardContent>
                <ul className="pricing-features">
                  <li><CheckCircle2 className="check-icon" />A1 (Sept - Dec 2026) and then A2 (Dec 2026 - Mar 2027)</li>
                  <li><CheckCircle2 className="check-icon" />100 hours of live coaching</li>
                  <li><CheckCircle2 className="check-icon" />All session recordings</li>
                  <li><CheckCircle2 className="check-icon" />LMS access</li>
                  <li><CheckCircle2 className="check-icon" />Complete learning kit</li>
                  <li><CheckCircle2 className="check-icon" />DELF/TEF/TCF aligned</li>
                </ul>
                <a href="https://shikshann.co/DLD-part-access" target="_blank" rel="noopener noreferrer">
                  <Button className="pricing-button" variant="outline">
                    Enroll in A1 Now
                  </Button>
                </a>
              </CardContent>
            </Card>

            <Card className="pricing-card featured-card">
              <div className="recommended-badge">Recommended</div>
              <CardHeader>
                <CardTitle className="pricing-title">Both Levels</CardTitle>
                <CardDescription className="pricing-description">Complete A1 & A2 Journey</CardDescription>
                <div className="pricing-amount featured-amount">
                  <span className="currency">₹</span>
                  <span className="amount">47,000</span>
                </div>
                <p className="pricing-period">200 hours • Sept 2026 - Mar 2027</p>
                <div className="savings-badge">
                  <Badge className="save-badge">Save ₹5,000</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="pricing-features">
                  <li><CheckCircle2 className="check-icon" />200 hours of live coaching</li>
                  <li><CheckCircle2 className="check-icon" />Complete A1 to A2 journey</li>
                  <li><CheckCircle2 className="check-icon" />All session recordings</li>
                  <li><CheckCircle2 className="check-icon" />LMS access</li>
                  <li><CheckCircle2 className="check-icon" />Complete learning kit</li>
                  <li><CheckCircle2 className="check-icon" />DELF/TEF/TCF aligned</li>
                </ul>
                <a href="https://shikshann.co/DLD-full-access" target="_blank" rel="noopener noreferrer">
                  <Button className="pricing-button featured-button">
                    Enroll Now
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="commitment-section">
        <div className="container-custom">
          <Card className="commitment-card">
            <CardHeader>
              <CardTitle className="commitment-title">This Program Works If You're Ready to Commit</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="commitment-intro">
                This isn't for casual learners. This is for people who are serious about mastering French and reaching their goals.
              </p>
              <div className="commitment-grid">
                <div className="commitment-item">
                  <div className="commitment-number">01</div>
                  <h4 className="commitment-heading">Total Commitment</h4>
                  <p className="commitment-text">Show up to every live session ready to participate and engage.</p>
                </div>
                <div className="commitment-item">
                  <div className="commitment-number">02</div>
                  <h4 className="commitment-heading">Daily Self-Study</h4>
                  <p className="commitment-text">Dedicate 30-60 minutes daily for practice outside of class time.</p>
                </div>
                <div className="commitment-item">
                  <div className="commitment-number">03</div>
                  <h4 className="commitment-heading">Consistent Attendance</h4>
                  <p className="commitment-text">Log in on time, every time. Your progress depends on consistency.</p>
                </div>
              </div>
              <div className="commitment-footer">
                <p>This level of commitment is what separates students who finish from those who don't. If you're ready, we're ready.</p>
              </div>
            </CardContent>
          </Card>

          <div className="cta-section-inline">
            <a href="#pricing">
              <Button size="lg" className="cta-primary">
                Reserve Your Spot
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* About Coach Section */}
      <section id="coach" className="coach-section">
        <div className="container-custom">
          <div className="coach-content">
            <div className="coach-image-wrapper">
              <img 
                src="https://lh3.googleusercontent.com/wtyVh4phN8j4XUL6MXxRLLjskftoVasrxt6BumfXb5n-J40_Hd_tigo2LoPOkHstNlPzcQId5LI-XaWlHA=w800"
                alt="Amrita Prabhu - French Confidence Coach"
                className="coach-image"
              />
            </div>
            <div className="coach-text">
              <Badge className="coach-badge">Your Coach</Badge>
              <h2 className="coach-name">Amrita Prabhu</h2>
              <p className="coach-title">French Confidence Coach</p>
              <div className="coach-description">
                <p>
                  Hi I'm Amrita Prabhu, your French Confidence Coach.
                </p>
                <p>
                  I got started on my learning journey simply out of love for languages and French was initially just a random choice. However, once I began, I got hooked and completed my C1 level in 3 years.
                </p>
                <p>
                  I've spent the last 14 years passionately working towards simplifying the learning journey for students. I have taught hundreds of students in my journey as a teacher, constructed French language curricula from scratch and created several course content materials.
                </p>
                <p>
                  Customizing lessons for hundreds of teens and young adults through dedicated 1-1 sessions for over 12 years gave me a lot of clarity on how the language concepts could be taught in a way that makes them enjoyable and result-oriented at the same time.
                </p>
                <p>
                  Equipped with all the experience I gathered over the years, I now take live group sessions for adults who are enthusiastic about learning the language, for certification or just for the love of the language and teens who study French as a part of their curriculum.
                </p>
              </div>
              <div className="coach-stats">
                <div className="stat-item">
                  <p className="stat-number">750+</p>
                  <p className="stat-label">Students Taught</p>
                </div>
                <div className="stat-item">
                  <p className="stat-number">14</p>
                  <p className="stat-label">Years Experience</p>
                </div>
                <div className="stat-item">
                  <p className="stat-number">20,000+</p>
                  <p className="stat-label">Teaching Hours</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Student Success Stories</h2>
            <p className="section-subtitle">Real results from students who committed to the journey</p>
          </div>

          <div className="testimonials-grid">
            <Card className="testimonial-card">
              <CardContent className="testimonial-content">
                <div className="testimonial-quote">
                  <p className="testimonial-text">
                    "It has been a fabulous 5 months of learning French with Amrita mam. I am amazed to see so much of a difference in my French capabilities in those 5 months. Her practical, structural and fun way of teaching French is truly outstanding. Classes are catered to meet each individual's goals in a meaningful way. Her passionate and polite way of teaching is what made the difference. Thank you so much for your support in my French learning journey."
                  </p>
                  <div className="testimonial-author">
                    <p className="author-name">Narayan Acharya</p>
                    <p className="author-result">Whistler, Canada • A2, April 2026</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="testimonial-card">
              <CardContent className="testimonial-content">
                <div className="testimonial-quote">
                  <p className="testimonial-text">
                    "A wonderful place to start off learning the basics of French with immensely interactive sessions and thorough activities which provide both opportunity and incentive to learn French in a way that does not seem forced, but rather fun."
                  </p>
                  <div className="testimonial-author">
                    <p className="author-name">Devaiah Bopanna</p>
                    <p className="author-result">Bangalore, India • A1, December 2019</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="testimonial-card">
              <CardContent className="testimonial-content">
                <div className="testimonial-quote">
                  <p className="testimonial-text">
                    "Amrita ma'am is one of the most qualified French teachers in Bangalore. Her passion for the language lends a unique style to her teaching, making her classes both enjoyable and inspiring. She draws from a wide variety of online sources and books to disrupt the monotony of using a single textbook. She will structure classes according to your pace of learning and takes time to answer questions in a way that sparks curiosity in learners. I began my study in the summer of 2016, before university, and the foundation I gained from my classes with her enabled me to pursue French for 3 semesters at the University of Edinburgh. Her notes helped me prepare for university level examinations. She provided constant support and encouragement even during my time at university. Amrita ma'am helped me discover a new language and taking her classes is probably the best decision you will make in your pursuit of French."
                  </p>
                  <div className="testimonial-author">
                    <p className="author-name">Sanjna Girish</p>
                    <p className="author-result">Edinburgh, Scotland • A2, May 2016</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="cta-section-inline">
            <a href="#pricing">
              <Button size="lg" className="cta-primary">
                Reserve Your Spot
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="container-custom">
          <div className="section-header">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">Everything you need to know about the program</p>
          </div>

          <div className="faq-wrapper">
            <Accordion type="single" collapsible className="faq-accordion">
              <AccordionItem value="item-1">
                <AccordionTrigger className="faq-question">
                  What if I miss a live class?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  Every live session is recorded and uploaded to the LMS within 24 hours. You'll have access to all recordings throughout the program and can review them anytime. However, we strongly encourage live attendance for the interactive learning experience and real-time practice.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger className="faq-question">
                  Do I need any prior French knowledge?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  Not at all! This program is designed for absolute beginners and takes you right from the basics through to DELF A2 level.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger className="faq-question">
                  What certification does this program prepare me for?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  This program prepares you for DELF (Diplôme d'Études en Langue Française), TEF (Test d'Évaluation de Français), and TCF (Test de Connaissance du Français) exams. The curriculum is aligned with the CEFR (Common European Framework of Reference for Languages) standards, which these certifications follow. By the end of A2, you'll be exam-ready for A2-level certifications.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4">
                <AccordionTrigger className="faq-question">
                  What if I can't commit to the full 6 months right now?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  That's okay — you can enrol, and learn at your own pace with access to the recordings and resources on the LMS for 1 year. You can revisit lessons whenever your schedule allows, up to 1 year. Alternatively, you can complete one level (A1) and enrol in a future A2 offering.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5">
                <AccordionTrigger className="faq-question">
                  How is this different from apps or YouTube?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  Apps and YouTube give you fragments of information. This program gives you a structured, examiner-informed curriculum, live instructor feedback, a clear path to an official certification, and a community of learners — something self-study alone can't replicate.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6">
                <AccordionTrigger className="faq-question">
                  Will I be fluent after this program?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  By the end of this program, you'll be confident and comfortable communicating in French at an A2 level — covering everyday conversations, reading, writing, and listening. Fluency is a continuous journey, and this course gives you a strong, exam-tested foundation to keep building on.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7">
                <AccordionTrigger className="faq-question">
                  Is this program conducted in English or French?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  The program is conducted primarily in English with progressive French immersion. As a beginner, you need explanations in a language you understand. Amrita teaches grammar concepts and instructions in English while gradually increasing French exposure throughout the program, so by A2 you're comfortable with more French-only interactions.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8">
                <AccordionTrigger className="faq-question">
                  How much time do I need to dedicate each week?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  30-60 minutes of dedicated self-study per day is essential to progress at the pace the course is designed for. This comes to at least 3-4 hours a week.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-9">
                <AccordionTrigger className="faq-question">
                  What happens after the 6 months — do I lose access to the LMS?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  Your access to the LMS extends to 1 year from the date of purchase. This gives you time to revise the course content and prepare for the certification.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-10">
                <AccordionTrigger className="faq-question">
                  When can I register for the actual DELF exam, and is it included in the course fee?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  You can register for the DELF exam on completion of the course. The exam fee, which is not included in the course fee, is to be paid directly to the institute conducting the International Certification Exams.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-11">
                <AccordionTrigger className="faq-question">
                  What if I have doubts outside class hours?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  Upon registration, you will receive a link to a dedicated WhatsApp Group. This group comprising the coach and the participants is an open forum for discussions and queries.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-12">
                <AccordionTrigger className="faq-question">
                  Is there a payment plan?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  Yes, you can choose to register for each level (A1 & A2) separately, though we strongly recommend you register for both together, so that you can avail a discount.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-13">
                <AccordionTrigger className="faq-question">
                  What if I don't clear the DELF / TEF / TCF exam on my first attempt?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  The course includes complete exam prep materials and guidance. There is no limit to the number of times you can attempt these certification exams. The course recordings can help you revisit concepts and prepare a second time.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-14">
                <AccordionTrigger className="faq-question">
                  Do I get a certificate of completion from Shikshann French?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  Yes. Shikshann provides a Certificate of Completion indicating the number of hours completed to achieve A1/A2 Level Competency.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-15">
                <AccordionTrigger className="faq-question">
                  What devices/tools do I need for the virtual classes?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  Ideally, log in from a laptop or tablet with a camera, for better participation. Log in from a quiet environment, free from distractions. Headphones will help with audio clarity – key for pronunciation. All sessions will be conducted on Zoom. Please keep a dedicated notebook and writing material with you.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-16">
                <AccordionTrigger className="faq-question">
                  What if the timing doesn't work for me?
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  We will keep you updated on upcoming batches with alternate timings, as we encourage live participation – the recordings are a useful fall back in case of occasional absenteeism, with prior notice.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <div className="cta-section-inline">
            <a href="#pricing">
              <Button size="lg" className="cta-primary">
                Reserve Your Spot
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="container-custom">
          <div className="final-cta-content">
            <h2 className="final-cta-headline">Your French Journey Starts Here!</h2>
            <p className="final-cta-subheadline">
              You can do it! Let's build your confidence and skills to reach your goal.
            </p>
            <div className="final-cta-buttons">
              <a href="#pricing">
                <Button size="lg" className="cta-primary large-cta">
                  Reserve Your Spot
                  <ChevronRight className="ml-2 h-6 w-6" />
                </Button>
              </a>
              <a href="https://shikshann.co/book-a-call" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="cta-secondary">
                  Book a Free Clarity Call
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="container-custom">
          <div className="footer-content">
            <div className="footer-section">
              <img 
                src="https://lh3.googleusercontent.com/99jJJYKPl05jFN8ae80tyFtkcJRf7dG9ScH8bJgD1oKZKUcPH28uijBA3a9LWMWfN6Uyt0_-rlzGuD0jBw=w1905"
                alt="Shikshann Logo"
                className="footer-logo"
              />
              <p className="footer-text">
                Professional French Coaching for<br />
                Certification and Confidence
              </p>
            </div>
            <div className="footer-section">
              <h4 className="footer-subheading">Quick Links</h4>
              <ul className="footer-links">
                <li><a href="#program">Program Details</a></li>
                <li><a href="#pricing">Pricing</a></li>
                <li><a href="#coach">About Coach</a></li>
                <li><a href="#faq">FAQ</a></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4 className="footer-subheading">Contact</h4>
              <p className="footer-text">Questions about the program?</p>
              <p className="footer-text">Reach out for more information.</p>
              <a href="mailto:connect@shikshann.com" className="footer-email">
                connect@shikshann.com
              </a>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2026 Shikshann. All rights reserved.
            </p>
            <div className="footer-legal">
              <a href="https://claude.ai/code/artifact/bf4e3814-67d9-49b1-a349-5089d84ca18a" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
              <span className="footer-separator">|</span>
              <a href="https://claude.ai/code/artifact/bf4e3814-67d9-49b1-a349-5089d84ca18a" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            </div>
          </div>
        </div>
      </footer>
      </main>
    </div>
  );
};

export default LandingPage;
